import Highcharts, { SeriesOptionsType } from "highcharts";
import { Dictionary, groupBy, isEqual, sum, uniq } from "lodash";
import { useEffect, useState } from "react";
import StoneXChart from "../../../../../StoneX/StoneXChart";
import { SeriesOption, VarianceReportConfiguration, VarianceReportRow } from "./Types";

interface VarianceValueChartProps {
  configuration: VarianceReportConfiguration;
  rows: VarianceReportRow[];
  pathToChart: string[] | undefined;
}


export function VarianceValueChart (props: VarianceValueChartProps) {

    const { configuration, rows, pathToChart } = props;

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

    const [rowsToChart, setRowsToChart] = useState<VarianceReportRow[]>(rows);
    
    const gray4 = getComputedStyle(document.documentElement).getPropertyValue('--gray4').trim();
    
    const seriesOptionToHighchartsSeries: {[key in SeriesOption]: (rowDictionary: Dictionary<VarianceReportRow[]>) => SeriesOptionsType} = {
        budgetValue: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Budget Value',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.budgetValue))),
            type: 'column',
            color: configuration.chart?.varianceValueChart?.series?.budgetValue?.color || '#39B6F2',
            yAxis: 0
        } as SeriesOptionsType),
        marketValue: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Market Value',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.marketValue))),
            type: 'column',
            color: configuration.chart?.varianceValueChart?.series?.marketValue?.color || '#4590DB',
            yAxis: 0
        } as SeriesOptionsType),
        blendedValue: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Blended Value',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.blendedValue))),
            type: 'column',
            color: configuration.chart?.varianceValueChart?.series?.blendedValue?.color || '#1327E1',
            yAxis: 0
        } as SeriesOptionsType),
        budgetPrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Budget Price',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.budgetPrice))),
            color: configuration.chart?.varianceValueChart?.series?.budgetPrice?.color || '#cc9502',
            type: 'line',
            yAxis: 1
        } as SeriesOptionsType),
        marketPrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Market Price',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.marketPrice))),
            color: configuration.chart?.varianceValueChart?.series?.marketPrice?.color || '#dc572e',
            type: 'line',
            yAxis: 1
        } as SeriesOptionsType),
        blendedPrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Weighted Average',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.blendedPrice))),
            color: configuration.chart?.varianceValueChart?.series?.blendedPrice?.color || '#aad100',
            type: 'line',
            yAxis: 1
        } as SeriesOptionsType),
    }

    function updateChart() {

        const isSameCommodity = uniq(rowsToChart.map(x => x.commodity?.commodityId)).length == 1;
        const commodity = isSameCommodity ? rowsToChart?.[0]?.commodity : undefined;

        const formattedNumber = (number?: number, series?: number) => {
            const decimals = series == 1 ? commodity?.displayPrecision ?? 2 : 0;

            let format = number == undefined
                ? undefined
                : "$" + Math.abs(number).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

            return format;
        };

        setChartOptions({
            title: {
                text: 'Variance'
            },
            tooltip: {
                useHTML: true, 
                formatter: function() {
                    return `
                        <div style="margin: 10px; font-weight: 700; line-height: 1.5em">
                            ${this.x}
                            <br/>
                            ${this.series.name}: ${formattedNumber(this.point.y, (this.series.yAxis as any).index)}
                        </div>
                    `;
                  }
            },
           plotOptions: {
                series: {
                states: {
                    inactive: {
                    opacity: 1 // Set opacity to 1 to prevent other series from fading
                    }
                }
                }
            },
            xAxis: {
                categories: getMonths(),
                labels: {
                    style: {
                        color: gray4,
                        fontWeight: '700'
                    }
                }
            },
            yAxis: [{
                allowDecimals: false,
                title: {
                    text: 'Dollar Value',
                    style: {
                        color: gray4,
                        fontWeight: '700'
                    }
                },
                labels: {
                    style: {
                        color: gray4,
                        fontWeight: '700'
                    }
                }},
                    ...isSameCommodity 
                        ? [{   // Secondary yAxis
                        gridLineWidth: 0,
                        title: {
                            text: 'Price',
                            style: {
                                color: gray4,
                                fontWeight: '700'
                            }
                        },
                        labels: {
                            format: '${value}',
                            style: {
                                color: gray4,
                                fontWeight: '700'
                            }
                        },
                        opposite: true // Makes this y-axis appear on the right    
                    }]
                    : []
                ],
            series: isSameCommodity  
                ? getSeries('budgetValue', 'marketValue', 'blendedValue', 'budgetPrice', 'marketPrice', 'blendedPrice')
                : getSeries('budgetValue', 'marketValue', 'blendedValue') 
        })
    }

    function getMonths() {
        var allMonths = rows.filter(x => !x.isAggregated).map(x => x.budgetDate?.label!);
        return uniq(allMonths);
    }

    function getSeries(...columns: SeriesOption[]) {
        const groupedRows = groupBy(rowsToChart, x => x.budgetDate?.label!);
        var series = columns.map(column => seriesOptionToHighchartsSeries[column](groupedRows));
        return series;
    }

    function updateRowsToChart() {
        let filteredRows = rows.filter(x => !x.isAggregated);

        if (pathToChart) {
            // Get all rows where the given path 'pathToChart' is a subset of the row's path
            const pathLength = pathToChart.length;
            filteredRows = filteredRows.filter(x => isEqual(x.path.slice(0, pathLength), pathToChart));
        } 

        setRowsToChart(filteredRows);
    }

    useEffect(updateRowsToChart, [rows, pathToChart]);
    useEffect(updateChart, [rowsToChart]);

    return <StoneXChart height='700px' highcharts={Highcharts} options={chartOptions} />;
}